import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, SlidersHorizontal, LogOut, GitCompare, Check } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { CATEGORY_META, PHONE, PHONE_TEL, BRAND_LOGO, MAIN_SITE_URL } from "@/showroomData";
import { CallBar } from "@/components/showroom/CallBar";
import { SpaDetailPanel } from "@/components/showroom/SpaDetailPanel";
import { LeadForm } from "@/components/showroom/LeadForm";
import { CompareView } from "@/components/showroom/CompareView";
import { RANGE } from "@/constants/testIds";

const CATS = ["spa", "swim_spa", "urban_pool", "sauna"];
const EMPTY_FILTERS = { brand: "", seats: "", size_label: "", beds: "", zone: "", config: "" };
const bedsLabel = (b) => (b === 0 ? "Seating only" : b === 1 ? "1 Massage Bed" : `${b} Massage Beds`);
const zoneMatch = (s, z) => (z === "single" ? s.zone === "Single Zone" : z === "dual" ? s.zone === "Dual Zone" : s.zone === z);
const configMatch = (s, c) => (s.config || "") === c;

export default function CatalogueRange() {
  const [params, setParams] = useSearchParams();
  const [all, setAll] = useState([]);
  const [category, setCategory] = useState(params.get("category") || "spa");
  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
    beds: params.get("beds") ?? "",
    zone: params.get("zone") || "",
    config: params.get("config") || "",
    seats: params.get("seats") || "",
  });
  const [selected, setSelected] = useState(null);
  const [lead, setLead] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    api.get("/spas").then((r) => setAll(r.data)).catch(() => {});
  }, []);

  const changeCat = (c) => {
    setCategory(c);
    setFilters({ ...EMPTY_FILTERS });
    setParams({ category: c });
    setCompareIds([]);
  };

  const toggleCompare = (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    setCompareIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= 4 ? prev : [...prev, id]));
  };

  const inCat = useMemo(() => all.filter((s) => s.category === category), [all, category]);

  const facets = useMemo(() => {
    const brands = [...new Set(inCat.map((s) => s.brand).filter(Boolean))];
    const seats = [...new Set(inCat.map((s) => s.seats).filter((x) => x != null))].sort((a, b) => a - b);
    const sizes = [...new Set(inCat.map((s) => s.size_label).filter(Boolean))];
    const beds = [...new Set(inCat.map((s) => s.massage_beds).filter((x) => x != null))].sort((a, b) => b - a);
    const hasZone = inCat.some((s) => s.zone);
    const hasConfig = category === "urban_pool";
    return { brands, seats, sizes, beds, hasZone, hasConfig };
  }, [inCat, category]);

  const filtered = useMemo(
    () =>
      inCat.filter(
        (s) =>
          (!filters.brand || s.brand === filters.brand) &&
          (!filters.seats || String(s.seats) === String(filters.seats)) &&
          (!filters.size_label || s.size_label === filters.size_label) &&
          (filters.beds === "" || String(s.massage_beds) === String(filters.beds)) &&
          (!filters.zone || zoneMatch(s, filters.zone)) &&
          (!filters.config || configMatch(s, filters.config))
      ),
    [inCat, filters]
  );

  const toggle = (key, val) =>
    setFilters((f) => ({ ...f, [key]: f[key] === String(val) ? "" : String(val) }));

  const hasFacets =
    facets.brands.length > 1 || facets.seats.length > 1 || facets.sizes.length > 1 || facets.beds.length > 1 || facets.hasZone || facets.hasConfig;

  const Chip = ({ active, onClick, children, testid }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors duration-200 ${
        active ? "border-cyan-400 bg-cyan-500/15 text-cyan-200" : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
      }`}
    >
      {children}
    </button>
  );

  const openCta = (ctaType, spa) => setLead({ ctaType, spa });

  return (
    <div className="min-h-screen bg-[#16171c] pb-28 text-white" data-testid={RANGE.page}>
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/50 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" data-testid={RANGE.backLink} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
            <ArrowLeft size={16} /> <span className="hidden sm:inline">Showroom</span>
          </Link>
          <div className="flex items-center gap-2.5">
            <img src={BRAND_LOGO} alt="Just Spas & Outdoors" className="h-8 rounded-md bg-white px-1.5 py-0.5 object-contain" />
            <span className="hidden font-display text-sm font-semibold tracking-tight sm:inline">Full Range</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={MAIN_SITE_URL}
              data-testid="range-exit-link"
              className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"
              title="Exit to spasnewcastle.com.au"
            >
              <LogOut size={15} /> <span className="hidden sm:inline">Exit</span>
            </a>
            <a href={`tel:${PHONE_TEL}`} className="text-sm font-medium text-slate-200 hover:text-cyan-300">{PHONE}</a>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
          Explore the full range
        </motion.h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          {CATEGORY_META[category]?.blurb} Prices aren't shown online — tap a model and register your interest, or call us on {PHONE}.
        </p>

        {/* Category tabs */}
        <div className="mt-6 flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => changeCat(c)}
              data-testid={`${RANGE.catTabPrefix}${c}`}
              className={`rounded-full px-5 py-2.5 font-display text-sm font-semibold tracking-tight transition-colors duration-200 ${
                category === c ? "bg-cyan-500 text-black" : "border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
              }`}
            >
              {CATEGORY_META[c].label}
            </button>
          ))}
        </div>

        {/* Filters */}
        {hasFacets && (
          <div className="mt-5 space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
              <SlidersHorizontal size={14} /> Filter
            </div>
            {facets.beds.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {facets.beds.map((b) => (
                  <Chip key={b} active={String(filters.beds) === String(b)} onClick={() => toggle("beds", b)} testid={`${RANGE.filterPrefix}beds-${b}`}>{bedsLabel(b)}</Chip>
                ))}
              </div>
            )}
            {facets.hasZone && (
              <div className="flex flex-wrap gap-2">
                <Chip active={filters.zone === "single"} onClick={() => toggle("zone", "single")} testid={`${RANGE.filterPrefix}zone-single`}>Open (Single Zone)</Chip>
                <Chip active={filters.zone === "dual"} onClick={() => toggle("zone", "dual")} testid={`${RANGE.filterPrefix}zone-dual`}>Dual Zone</Chip>
              </div>
            )}
            {facets.hasConfig && (
              <div className="flex flex-wrap gap-2">
                <Chip active={filters.config === "plunge"} onClick={() => toggle("config", "plunge")} testid={`${RANGE.filterPrefix}config-plunge`}>Plunge / Swim</Chip>
                <Chip active={filters.config === "open"} onClick={() => toggle("config", "open")} testid={`${RANGE.filterPrefix}config-open`}>Open Spa</Chip>
                <Chip active={filters.config === "closed"} onClick={() => toggle("config", "closed")} testid={`${RANGE.filterPrefix}config-closed`}>Closed Spa</Chip>
              </div>
            )}
            {facets.brands.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {facets.brands.map((b) => (
                  <Chip key={b} active={filters.brand === b} onClick={() => toggle("brand", b)} testid={`${RANGE.filterPrefix}brand-${b.replace(/\s+/g, "-").toLowerCase()}`}>{b}</Chip>
                ))}
              </div>
            )}
            {facets.seats.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {facets.seats.map((s) => (
                  <Chip key={s} active={String(filters.seats) === String(s)} onClick={() => toggle("seats", s)} testid={`${RANGE.filterPrefix}seats-${s}`}>{s} Person</Chip>
                ))}
              </div>
            )}
            {facets.sizes.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {facets.sizes.map((s) => (
                  <Chip key={s} active={filters.size_label === s} onClick={() => toggle("size_label", s)} testid={`${RANGE.filterPrefix}size-${s.replace(/[^a-z0-9]/gi, "")}`}>{s}</Chip>
                ))}
              </div>
            )}
          </div>
        )}

        <p className="mt-5 text-sm text-slate-500" data-testid={RANGE.count}>{filtered.length} models</p>

        {/* Grid */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
          {filtered.map((spa, i) => (
            <motion.div
              key={spa.id}
              role="button"
              tabIndex={0}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.4) }}
              onClick={() => setSelected(spa)}
              data-testid={`${RANGE.cardPrefix}${spa.id}`}
              className="group cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] text-left transition-transform duration-200 hover:-translate-y-1 hover:border-cyan-400/40"
            >
              <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-white/5 to-transparent">
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => toggleCompare(spa.id, e)}
                  data-testid={`${RANGE.comparePrefix}${spa.id}`}
                  className={`absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold transition-colors ${compareIds.includes(spa.id) ? "bg-cyan-500 text-black" : "bg-black/55 text-white ring-1 ring-white/20 hover:bg-black/75"}`}
                >
                  {compareIds.includes(spa.id) ? <><Check size={11} /> Added</> : "+ Compare"}
                </span>
                {spa.image_url ? (
                  <img src={mediaUrl(spa.image_url)} alt={spa.name} loading="lazy" className="h-full w-full object-contain p-3 transition-transform duration-300 group-hover:scale-105" />
                ) : null}
              </div>
              <div className="p-3">
                {spa.brand ? <div className="text-[10px] uppercase tracking-wide text-amber-400">{spa.brand}</div> : null}
                <div className="mt-0.5 font-display text-sm font-semibold leading-tight">{spa.name}</div>
                <div className="mt-1 flex flex-wrap gap-1 text-[11px] text-slate-400">
                  {spa.seats ? <span>{spa.seats} Person</span> : null}
                  {spa.size_label ? <span>· {spa.size_label}</span> : null}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {compareIds.length > 0 && (
        <div data-testid={RANGE.compareBar} className="fixed inset-x-0 bottom-24 z-40 mx-auto flex w-[min(92%,640px)] items-center justify-between gap-3 rounded-full border border-white/10 bg-[#1d1f26]/95 px-4 py-2.5 shadow-xl backdrop-blur-xl">
          <span className="text-sm text-slate-300">{compareIds.length} selected{compareIds.length < 2 ? " · add one more" : ""}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setCompareIds([])} data-testid={RANGE.compareClear} className="rounded-full px-3 py-1.5 text-sm text-slate-400 hover:text-white">Clear</button>
            <button onClick={() => setShowCompare(true)} disabled={compareIds.length < 2} data-testid={RANGE.compareOpen} className="inline-flex items-center gap-2 rounded-full bg-cyan-500 px-4 py-1.5 text-sm font-semibold text-black hover:bg-cyan-400 disabled:opacity-40">
              <GitCompare size={15} /> Compare
            </button>
          </div>
        </div>
      )}
      {showCompare && (
        <CompareView spas={compareIds.map((id) => inCat.find((s) => s.id === id)).filter(Boolean)} onClose={() => setShowCompare(false)} />
      )}

      <SpaDetailPanel spa={selected} onClose={() => setSelected(null)} onCta={openCta} />
      {lead && (
        <LeadForm ctaType={lead.ctaType} spa={lead.spa} room={CATEGORY_META[category]?.label} onClose={() => setLead(null)} />
      )}
      <CallBar />
    </div>
  );
}
